import React, { useEffect, useState } from "react";
import { ColorResult } from "react-color";

import { CreatomatePropertyValue } from "chq";
import { TextState, Preview } from "@creatomate/preview";

import { Trans } from "app/utils";
import general from "constants/general";

import ColorPicker from "components/stories/video-studio/common/ColorPicker";
import getTextFontEffect from "components/stories/video-studio/utils/creatomate/getTextFontEffect";
import { getEffectColorByOrg, getEffectProperty, getHexFromRgbObject, getHslFromRgba, getRgbColor, getRgbObjFromString } from "components/stories/video-studio/utils";
import TextEffects from "./text-effects/TextEffects";

interface FontEffectsProps {
  activeElement: TextState;
  disabled?: boolean;
  organization: string;
  onSelectedEffect: (value: string) => void;
  onSubtitleEffectChange?: (properties: { property: string; value: string }[]) => Promise<void>;
  onSubtitlePropChange?: (propertyName: string, value: CreatomatePropertyValue) => Promise<void>;
  preview: Preview;
  selectedEffect: string;
}

const FontEffects: React.FC<FontEffectsProps> = ({
  activeElement,
  disabled,
  organization,
  onSelectedEffect,
  onSubtitleEffectChange,
  onSubtitlePropChange,
  preview,
  selectedEffect
}) => {
  const [effectColor, setEffectColor] = useState<ColorResult>(general.DEFAULT_COLOR);

  useEffect(
    () => {
      setEffectColor(getEffectColorByOrg(organization));
    }, [organization]
  );

  useEffect(
    () => {
      if (activeElement.source.type === "text") {
        const effect = getTextFontEffect(activeElement);
        const color = activeElement.source[getEffectProperty(effect)];
        const rgbaObj = getRgbObjFromString(color);
        if (color) {
          setEffectColor({
            hex: getHexFromRgbObject(rgbaObj),
            rgb: rgbaObj,
            hsl: getHslFromRgba(rgbaObj)
          });
        }
      }
    }, [activeElement]
  );

  const handleEffectColorChange = async (color: ColorResult) => {
    setEffectColor(color);
    const efffectColorProperty = getEffectProperty(selectedEffect);
    if (efffectColorProperty) {
      if (onSubtitlePropChange) {
        await onSubtitlePropChange(efffectColorProperty, getRgbColor(color));
      } else {
        await preview.applyModifications({
          [`${activeElement.source.id}.${efffectColorProperty}`]: getRgbColor(color)
        });
      }
    }
  };

  return (
    <div className="FontEffects">
      <div className="FontEffects-Main">
        <TextEffects
          activeElement={activeElement}
          closeOnOutsideClick
          color={effectColor}
          disabled={disabled}
          onSelectedEffect={onSelectedEffect}
          onSubtitleEffectChange={onSubtitleEffectChange}
          preview={preview}
          selectedEffect={selectedEffect}
        />
      </div>
      <div className="FontEffects-Secondary">
        <span className="chq-ffd--lb"><Trans>Effect Color</Trans></span>
        <ColorPicker
          disabled={selectedEffect === general.DEFAULT_EFFECT}
          initialColor={effectColor}
          onChangeColor={handleEffectColorChange}
        />
      </div>
    </div>
  );
};

export default FontEffects;
